/**
 * universeStatus: the page's two readings of the ring arcs, the status sentence
 * under the title and the next move under the stage.
 *
 * Pure derivation from the laid-out rings. Every number is a count of arcs or
 * facts the graph already holds; nothing here guesses.
 */
import type { Ring } from "./universeLayout";
import { OTHER_SUBJECT_KEY } from "./universeTaxonomy";
import { numberWord } from "./universeText";

/** An empty group: the label people read, and the concept an agent files by. */
export type EmptyGroup = { label: string; concept?: string };

export type RivalGaps = {
  key: string;
  name: string;
  groups: number;
  /** Empty groups, in facet order. */
  empty: EmptyGroup[];
};

export type SubjectGaps = {
  key: string;
  /** "Brand", "Competitors". */
  label: string;
  /** Countable groups: facet arcs, or rivals × rival facets. */
  groups: number;
  uncharted: number;
  /** Unique facts filed in this subject, cross-filed ones included. */
  facts: number;
  /** True when the Competitors ring is split by named rivals. */
  rivalMode: boolean;
  /** Empty groups for an ordinary subject, in facet order. */
  empty: EmptyGroup[];
  /** Per rival, in the list's order. */
  rivals: RivalGaps[];
};

const titleCase = (s: string) => s.charAt(0) + s.slice(1).toLowerCase();

/** Per-subject group and gap counts. OTHER is a holding bucket, not a subject,
 *  so it never appears here. */
export function computeSubjectGaps(rings: Ring[]): SubjectGaps[] {
  return rings
    .filter((ring) => ring.id !== OTHER_SUBJECT_KEY)
    .map((ring) => {
      const countable = ring.arcs.filter((arc) => arc.countable);
      const rivalMode = ring.arcs.some((arc) => arc.entityKey);
      const rivals: RivalGaps[] = [];
      if (rivalMode) {
        for (const arc of countable) {
          const key = arc.entityKey ?? "";
          let rival = rivals.find((r) => r.key === key);
          if (!rival) {
            rival = { key, name: arc.entityName ?? "", groups: 0, empty: [] };
            rivals.push(rival);
          }
          rival.groups += 1;
          if (!arc.charted) rival.empty.push({ label: arc.label, concept: arc.concept });
        }
      }
      return {
        key: ring.id,
        label: titleCase(ring.label),
        groups: countable.length,
        uncharted: countable.filter((arc) => !arc.charted).length,
        facts: new Set(ring.arcs.flatMap((arc) => arc.factIds)).size,
        rivalMode,
        empty: rivalMode
          ? []
          : countable.filter((arc) => !arc.charted).map((arc) => ({ label: arc.label, concept: arc.concept })),
        rivals,
      };
    });
}

export type SentencePart = { text: string; strong?: boolean };

/**
 * "70 facts across five subjects. The rivals are the thin part: 10 of their 20
 * groups are still uncharted." The thin part is the subject with the most
 * uncharted groups; ties go to the earlier subject.
 */
export function statusSentence(total: number, gaps: SubjectGaps[]): SentencePart[] {
  // Nothing learned yet: say so, rather than "0 facts across zero subjects".
  if (total === 0) return [{ text: "No facts yet.", strong: true }];
  const parts: SentencePart[] = [
    { text: `${total} fact${total === 1 ? "" : "s"}`, strong: true },
    { text: ` across ${numberWord(gaps.length)} subject${gaps.length === 1 ? "" : "s"}. ` },
  ];
  const thin = gaps.reduce<SubjectGaps | null>(
    (best, g) => (g.uncharted > 0 && (!best || g.uncharted > best.uncharted) ? g : best),
    null,
  );
  if (!thin) {
    parts.push({ text: "Every group has at least one fact." });
    return parts;
  }
  const verb = thin.uncharted === 1 ? "is" : "are";
  if (thin.rivalMode) {
    parts.push(
      { text: "The rivals", strong: true },
      { text: " are the thin part: " },
      { text: `${thin.uncharted} of their ${thin.groups} groups`, strong: true },
      { text: ` ${verb} still uncharted.` },
    );
  } else {
    parts.push(
      { text: thin.label, strong: true },
      { text: " is the thin part: " },
      { text: `${thin.uncharted} of its ${thin.groups} groups`, strong: true },
      { text: ` ${verb} still uncharted.` },
    );
  }
  return parts;
}

/**
 * The next move carries only the action: where the gaps are, and the button.
 * The count of uncharted groups is the status sentence's job, so it is not
 * repeated here.
 */
export type NextMove = {
  detail: string;
  /** The button label. */
  cta: string;
  /** What the button prefills in the agent composer. */
  prompt: string;
};

function rivalsSorted(g: SubjectGaps): RivalGaps[] {
  // Array.prototype.sort is stable, so equal counts keep the list's order.
  return [...g.rivals].sort((a, b) => b.empty.length - a.empty.length);
}

/** The first `max` names, then "+N more", so the line stays one short sentence. */
function capList(names: string[], max: number): string {
  if (names.length <= max) return names.join(", ");
  return `${names.slice(0, max).join(", ")} +${names.length - max} more`;
}

function rivalDetail(g: SubjectGaps): string {
  const sorted = rivalsSorted(g);
  const withGaps = sorted.filter((r) => r.empty.length > 0);
  const full = sorted.filter((r) => r.empty.length === 0).map((r) => r.name);
  const head = withGaps
    .slice(0, 2)
    .map((r, i) => (i === 0 ? `${r.name} has ${r.empty.length} of ${r.groups} groups empty` : `${r.name} ${r.empty.length}`))
    .join(", ");
  const extra = withGaps.length > 2 ? ` +${withGaps.length - 2} more` : "";
  const sentences = head ? [`${head}${extra}.`] : [];
  if (full.length === 1) sentences.push(`${full[0]} is fully charted.`);
  else if (full.length > 1) sentences.push(`${full.length} rivals are fully charted.`);
  return sentences.join(" ");
}

/**
 * The agent files facts by CONCEPT NAME, so a display label alone ("What they
 * stand for") routes nowhere. Each group reads as its label with the concept in
 * parentheses; a rival's Overview has no concept of its own and says what it is.
 */
function promptGroup(group: EmptyGroup): string {
  if (group.concept) return `${group.label} (${group.concept})`;
  return `${group.label} (general facts about this rival)`;
}

function promptItems(g: SubjectGaps): string[] {
  if (g.rivalMode) {
    return rivalsSorted(g)
      .filter((r) => r.empty.length > 0)
      .map((r) => `${r.name}: ${r.empty.map(promptGroup).join(", ")}`);
  }
  return g.empty.length ? [`${g.label}: ${g.empty.map(promptGroup).join(", ")}`] : [];
}

/** The one next move, or null when every group holds a fact. */
export function nextMove(gaps: SubjectGaps[]): NextMove | null {
  const open = gaps.filter((g) => g.uncharted > 0);
  if (!open.length) return null;
  let detail: string;
  let cta: string;
  if (open.length === 1 && open[0].rivalMode) {
    detail = rivalDetail(open[0]);
    cta = "Chart rivals";
  } else if (open.length === 1) {
    const g = open[0];
    detail = `Empty in ${g.label}: ${capList(g.empty.map((e) => e.label), 3)}.`;
    cta = `Chart ${g.label.toLowerCase()}`;
  } else {
    detail = `Gaps in ${capList(open.map((g) => g.label), 3)}.`;
    cta = "Chart the gaps";
  }
  const items = open.flatMap(promptItems);
  const prompt = `${cta}. Fill these empty groups: ${items.join("; ")}.`;
  return { detail, cta, prompt };
}
